import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Rocket, Compass, ShieldCheck, TrendingUp, ClipboardList,
  CheckCircle2, ArrowRight, ExternalLink, MessageSquare, History,
  Filter, CheckSquare, ChevronDown, X, PlusCircle, AlertTriangle, Bell, Moon,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const COLUMNS = [
  { key: "todo",         label: "To Do",       tint: "bg-slate-50 ring-slate-200" },
  { key: "in_progress",  label: "In Progress", tint: "bg-amber-50 ring-amber-200" },
  { key: "done",         label: "Done",        tint: "bg-emerald-50 ring-emerald-200" },
  { key: "skipped",      label: "Skipped",     tint: "bg-slate-50 ring-slate-200 opacity-80" },
];

const OUTCOME = {
  build:        { label: "Build",         Icon: Rocket,       tone: "bg-orange-50 text-orange-700 ring-orange-200" },
  be_found:     { label: "Be Found",      Icon: Compass,      tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  build_trust:  { label: "Build Trust",   Icon: ShieldCheck,  tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  convert_grow: { label: "Convert & Grow", Icon: TrendingUp,  tone: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
};
const PRIORITY = {
  p0: { label: "P0", tone: "bg-rose-100 text-rose-800 ring-rose-300" },
  p1: { label: "P1", tone: "bg-amber-100 text-amber-800 ring-amber-300" },
  p2: { label: "P2", tone: "bg-slate-100 text-slate-700 ring-slate-300" },
};

export default function GrowthTaskCenter() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks]     = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {}, completion_pct: 0 });
  const [role, setRole]       = useState("client");
  const [audits, setAudits]   = useState([]);
  const [overdue, setOverdue] = useState(0);
  const [remindBusy, setRemindBusy] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: true, weekday: null, hour_utc: null });
  const [filter, setFilter]   = useState({ outcome: "all", audit: "all", priority: "all" });
  const [selected, setSelected]   = useState(new Set());
  const [openTask, setOpenTask]   = useState(null);
  const [bulkPanel, setBulkPanel] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [{ data: t }, { data: a }, { data: od }, { data: sch }] = await Promise.all([
        axios.get(`${API}/growth-audit/tasks`, { headers: httpAuth() }),
        axios.get(`${API}/growth-audit/my-audits`, { headers: httpAuth() }),
        axios.get(`${API}/growth-audit/reminders/overdue`, { headers: httpAuth() }),
        axios.get(`${API}/growth-audit/reminders/schedule`, { headers: httpAuth() }),
      ]);
      setTasks(t.tasks || []);
      setSummary(t.summary || {});
      setRole(t.role || "client");
      setAudits(a.audits || []);
      setOverdue(od.count || 0);
      setSchedule(sch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your task queue.");
    }
  }, []);

  const saveSchedule = async (patch) => {
    const next = { ...schedule, ...patch };
    setSchedule(next);
    try {
      await axios.post(`${API}/growth-audit/reminders/schedule`, patch,
                        { headers: httpAuth() });
      toast.success("Reminder schedule updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const emailMeReminder = async () => {
    setRemindBusy(true);
    try {
      const { data } = await axios.post(`${API}/growth-audit/reminders/send-now`,
        {}, { headers: httpAuth() });
      toast.success(data.sent
        ? `Reminder emailed · ${data.task_count} P0 task${data.task_count !== 1 ? "s" : ""}`
        : `Reminder ready (email off) · ${data.task_count} task${data.task_count !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No overdue P0 tasks");
    } finally { setRemindBusy(false); }
  };

  const snoozeReminders = async (days = 3) => {
    setRemindBusy(true);
    try {
      const { data } = await axios.post(`${API}/growth-audit/reminders/snooze`,
        { days }, { headers: httpAuth() });
      toast.success(`Snoozed until ${data.snoozed_until}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snooze failed");
    } finally { setRemindBusy(false); }
  };

  useEffect(() => {
    document.title = "Growth Task Center | WebsiteTech.pro";
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    (async () => { await reload(); setLoading(false); })();
  }, [user, reload]);

  const filtered = useMemo(() => tasks.filter(t => (
    (filter.outcome  === "all" || t.outcome  === filter.outcome) &&
    (filter.audit    === "all" || t.audit_id === filter.audit) &&
    (filter.priority === "all" || t.priority === filter.priority)
  )), [tasks, filter]);

  const byCol = useMemo(() => {
    const m = { todo: [], in_progress: [], done: [], skipped: [] };
    filtered.forEach(t => (m[t.status || "todo"] ||= []).push(t));
    // sort: p0 first, then created_at desc
    Object.keys(m).forEach(k => m[k].sort((a, b) => {
      const p = { p0: 0, p1: 1, p2: 2 };
      if ((p[a.priority] ?? 3) !== (p[b.priority] ?? 3))
        return (p[a.priority] ?? 3) - (p[b.priority] ?? 3);
      return (b.created_at || "").localeCompare(a.created_at || "");
    }));
    return m;
  }, [filtered]);

  const toggleSelect = (tid) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(tid) ? n.delete(tid) : n.add(tid);
      return n;
    });
  };

  const setStatus = async (tid, status) => {
    // optimistic
    setTasks(prev => prev.map(t => t.finding_id === tid ? { ...t, status } : t));
    try {
      await axios.post(`${API}/growth-audit/tasks/${tid}`, { status },
                        { headers: httpAuth() });
      toast.success(`Moved to ${COLUMNS.find(c => c.key === status)?.label}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      reload();
    }
  };

  const bulkApply = async (updates) => {
    try {
      const { data } = await axios.post(`${API}/growth-audit/tasks/bulk`, {
        task_ids: Array.from(selected), ...updates,
      }, { headers: httpAuth() });
      toast.success(`Updated ${data.modified} task(s)`);
      setSelected(new Set()); setBulkPanel(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    }
  };

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="growth-tasks-signin">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <ClipboardList className="w-10 h-10 text-slate-400 mx-auto" />
          <h1 className="text-3xl font-bold text-slate-900 mt-3">Growth Task Center</h1>
          <p className="text-slate-600 mt-2">
            Sign in to see the fixes from your growth audit — organized like a project board so you can knock them out one by one.
          </p>
          <Link to="/login?redirect=/growth-tasks"
                data-testid="growth-tasks-signin-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-bold">
            Sign in <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-4 text-sm text-slate-500">
            No audit yet? <Link className="text-orange-600 font-bold underline" to="/growth-audit">Run your free growth audit →</Link>
          </div>
        </main>
      </div>
    );
  }

  const isEmpty = tasks.length === 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="growth-tasks-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* ---- Header ---- */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
              WebsiteTech · Growth System
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1"
                data-testid="growth-tasks-title">Growth Task Center</h1>
            <p className="text-slate-600 mt-1 max-w-2xl">
              Every fix from your growth audit becomes a task here.
              Drag it forward as you make progress — {role === "admin" ? "you can edit any task." : "you can edit tasks assigned to you."}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/growth-audit"
                  data-testid="growth-tasks-new-audit"
                  className="inline-flex items-center gap-1 rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm font-bold text-slate-800">
              <PlusCircle className="w-4 h-4" /> New audit
            </Link>
          </div>
        </div>

        {/* ---- Progress ---- */}
        <div className="mt-6 rounded-xl bg-white ring-1 ring-slate-200 p-5"
             data-testid="growth-tasks-progress">
          <div className="flex flex-wrap items-baseline gap-3">
            <div className="text-3xl font-extrabold text-slate-900">
              {summary.completion_pct || 0}%
            </div>
            <div className="text-sm text-slate-600">
              of your growth plan is done · {summary.by_status?.done || 0} / {summary.total || 0} tasks complete
            </div>
          </div>
          <div className="mt-3 w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500"
                 style={{ width: `${summary.completion_pct || 0}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3 text-center">
            {COLUMNS.map(c => (
              <div key={c.key} className="rounded-lg ring-1 ring-slate-200 p-2 bg-slate-50"
                   data-testid={`growth-tasks-count-${c.key}`}>
                <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">{c.label}</div>
                <div className="text-2xl font-extrabold text-slate-900 mt-1">
                  {summary.by_status?.[c.key] || 0}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ---- Reminder schedule ---- */}
        {user && (
          <div className="mt-4 rounded-xl bg-white ring-1 ring-slate-200 p-4"
               data-testid="reminder-schedule">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Reminder delivery
                </div>
                <div className="text-sm text-slate-700 mt-0.5">
                  When should overdue-task nudges land? Leave blank for "any weekday, morning UTC".
                </div>
              </div>
              <label className="text-xs text-slate-600 flex items-center gap-1">
                <input type="checkbox" checked={schedule.enabled}
                        onChange={(e) => saveSchedule({ enabled: e.target.checked })}
                        data-testid="reminder-schedule-enabled" />
                On
              </label>
              <select value={schedule.weekday ?? ""}
                       onChange={(e) => saveSchedule({ weekday: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                       data-testid="reminder-schedule-weekday"
                       className="rounded-lg ring-1 ring-slate-200 px-2 py-1.5 text-xs">
                <option value="">Any weekday</option>
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <option key={i} value={i}>{d}</option>
                ))}
              </select>
              <select value={schedule.hour_utc ?? ""}
                       onChange={(e) => saveSchedule({ hour_utc: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                       data-testid="reminder-schedule-hour"
                       className="rounded-lg ring-1 ring-slate-200 px-2 py-1.5 text-xs">
                <option value="">Any morning</option>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{`${String(i).padStart(2, "0")}:00 UTC`}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ---- Overdue P0 banner ---- */}
        {overdue > 0 && (
          <div className="mt-4 rounded-xl bg-gradient-to-r from-rose-600 to-orange-500 text-white p-4 flex flex-wrap items-center gap-3 justify-between"
               data-testid="growth-tasks-overdue-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 shrink-0" />
              <div>
                <div className="font-bold">
                  {overdue} P0 growth task{overdue !== 1 ? "s" : ""} overdue
                </div>
                <div className="text-white/90 text-sm">
                  These fixes move your score the most — knock one out today to keep momentum.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => snoozeReminders(1)} disabled={remindBusy}
                      data-testid="growth-tasks-snooze-1d"
                      className="rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                <Moon className="w-3.5 h-3.5" /> Snooze 1 day
              </button>
              <button onClick={() => snoozeReminders(3)} disabled={remindBusy}
                      data-testid="growth-tasks-snooze"
                      className="rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                <Moon className="w-3.5 h-3.5" /> Snooze 3 days
              </button>
              <button onClick={() => snoozeReminders(7)} disabled={remindBusy}
                      data-testid="growth-tasks-snooze-1w"
                      className="rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                <Moon className="w-3.5 h-3.5" /> Snooze 1 week
              </button>
              <button onClick={emailMeReminder} disabled={remindBusy}
                      data-testid="growth-tasks-email-reminder"
                      className="rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                {remindBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Email me a reminder
              </button>
            </div>
          </div>
        )}

        {/* ---- Empty state ---- */}
        {isEmpty && (
          <div className="mt-8 rounded-xl bg-white ring-1 ring-slate-200 p-8 text-center"
               data-testid="growth-tasks-empty">
            <ClipboardList className="w-10 h-10 text-slate-400 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900 mt-3">No tasks yet</h2>
            <p className="text-slate-600 mt-1 max-w-xl mx-auto">
              Run a free growth audit — every finding becomes a card you can work like a mini Kanban board.
            </p>
            <Link to="/growth-audit"
                  data-testid="growth-tasks-empty-cta"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-bold">
              Run my free audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!isEmpty && (
          <>
            {/* ---- Filters ---- */}
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="growth-tasks-filters">
              <div className="inline-flex items-center gap-1 text-slate-500 text-xs uppercase tracking-widest font-bold">
                <Filter className="w-3.5 h-3.5" /> Filter
              </div>
              <Select value={filter.outcome}
                      onChange={(v) => setFilter(f => ({ ...f, outcome: v }))}
                      testid="filter-outcome"
                      options={[
                        { v: "all", l: "All outcomes" },
                        { v: "build", l: "Build" },
                        { v: "be_found", l: "Be Found" },
                        { v: "build_trust", l: "Build Trust" },
                        { v: "convert_grow", l: "Convert & Grow" },
                      ]} />
              <Select value={filter.priority}
                      onChange={(v) => setFilter(f => ({ ...f, priority: v }))}
                      testid="filter-priority"
                      options={[
                        { v: "all", l: "All priorities" },
                        { v: "p0", l: "P0 (urgent)" }, { v: "p1", l: "P1" }, { v: "p2", l: "P2" },
                      ]} />
              {audits.length > 1 && (
                <Select value={filter.audit}
                        onChange={(v) => setFilter(f => ({ ...f, audit: v }))}
                        testid="filter-audit"
                        options={[
                          { v: "all", l: "All audits" },
                          ...audits.map(a => ({ v: a.id, l: a.domain || a.url })),
                        ]} />
              )}
              {role === "admin" && selected.size > 0 && (
                <button onClick={() => setBulkPanel(true)}
                        data-testid="growth-tasks-bulk-open"
                        className="ml-auto inline-flex items-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold">
                  <CheckSquare className="w-4 h-4" /> Bulk actions ({selected.size})
                </button>
              )}
            </div>

            {/* ---- Kanban ---- */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
                 data-testid="growth-tasks-board">
              {COLUMNS.map(col => (
                <div key={col.key}
                     className={`rounded-xl ring-1 ${col.tint} p-3`}
                     data-testid={`col-${col.key}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
                      {col.label}
                    </div>
                    <div className="text-xs text-slate-500">{byCol[col.key].length}</div>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {byCol[col.key].map(t => (
                      <TaskCard key={t.finding_id} task={t}
                                selectable={role === "admin"}
                                selected={selected.has(t.finding_id)}
                                onSelect={() => toggleSelect(t.finding_id)}
                                onOpen={() => setOpenTask(t)}
                                onStatus={(s) => setStatus(t.finding_id, s)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {openTask && <TaskDrawer task={openTask} role={role}
                                   onClose={() => setOpenTask(null)}
                                   onSaved={() => { setOpenTask(null); reload(); }} />}

        {bulkPanel && <BulkPanel count={selected.size}
                                    onClose={() => setBulkPanel(false)}
                                    onApply={bulkApply} />}
      </main>
    </div>
  );
}

/* ------------------------------- Task card ------------------------------- */
function TaskCard({ task, selectable, selected, onSelect, onOpen, onStatus }) {
  const out = OUTCOME[task.outcome] || OUTCOME.build;
  const pri = PRIORITY[task.priority] || PRIORITY.p2;
  const OutIcon = out.Icon;
  const overdue = task.due && task.status !== "done" && task.status !== "skipped"
                    && task.due < new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200 hover:ring-slate-400 transition p-3"
         data-testid={`task-card-${task.finding_id}`}>
      <div className="flex items-start gap-2">
        {selectable && (
          <input type="checkbox" checked={selected} onChange={onSelect}
                  className="mt-1"
                  data-testid={`task-select-${task.finding_id}`} />
        )}
        <button onClick={onOpen} className="text-left flex-1 min-w-0"
                data-testid={`task-open-${task.finding_id}`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${pri.tone}`}>{pri.label}</span>
            <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${out.tone}`}>
              <OutIcon className="w-3 h-3" /> {out.label}
            </span>
            {overdue && (
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset bg-rose-50 text-rose-700 ring-rose-200">
                overdue
              </span>
            )}
          </div>
          <div className="font-bold text-slate-900 text-sm mt-1.5">{task.title}</div>
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{task.action}</div>
          <div className="mt-2 flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] text-slate-500">
            <span className="truncate max-w-[130px]">{(task.owner || "unassigned").split("@")[0]}</span>
            {task.due && <span>· due {task.due}</span>}
            {task.url && <span className="truncate max-w-[100px]">· {new URL(task.url).hostname}</span>}
          </div>
        </button>
      </div>
      <div className="mt-2 flex gap-1">
        {["todo", "in_progress", "done", "skipped"]
          .filter(s => s !== task.status)
          .slice(0, 2)
          .map(s => (
            <button key={s} onClick={() => onStatus(s)}
                    className="text-[10px] rounded-md ring-1 ring-slate-200 hover:bg-slate-50 px-1.5 py-0.5 text-slate-600"
                    data-testid={`task-quick-${task.finding_id}-${s}`}>
              → {COLUMNS.find(c => c.key === s).label}
            </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- Drawer ---------------------------------- */
function TaskDrawer({ task, role, onClose, onSaved }) {
  const [t, setT]     = useState(task);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/growth-audit/tasks/${task.finding_id}/log`,
                { headers: httpAuth() })
         .then(r => setLog(r.data.log || []))
         .catch(() => {});
  }, [task.finding_id]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/growth-audit/tasks/${task.finding_id}`,
                                          patch, { headers: httpAuth() });
      setT(cur => ({ ...cur, ...data }));
      toast.success("Saved");
      // refresh log
      const r = await axios.get(`${API}/growth-audit/tasks/${task.finding_id}/log`,
                                  { headers: httpAuth() });
      setLog(r.data.log || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const out = OUTCOME[t.outcome] || OUTCOME.build;
  const OutIcon = out.Icon;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="task-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <aside className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-2">
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${out.tone}`}>
                <OutIcon className="w-3 h-3" /> {out.label}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                {(t.category || "").replace("_", " ")}
              </span>
            </div>
            <h2 className="font-bold text-slate-900 mt-2">{t.title}</h2>
            <p className="text-sm text-slate-700 mt-1">{t.action}</p>
          </div>
          <button onClick={onClose} data-testid="task-drawer-close"
                  className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {t.evidence && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Evidence</div>
              <div className="text-xs bg-slate-50 rounded-lg p-2 font-mono text-slate-700 break-all">
                {t.evidence}
              </div>
              {t.url && (
                <a href={t.url} target="_blank" rel="noreferrer"
                    className="text-xs inline-flex items-center gap-1 text-orange-600 hover:underline mt-1"
                    data-testid="task-drawer-view-url">
                  Open page <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
          <Row label="Status">
            <select value={t.status} onChange={(e) => save({ status: e.target.value })}
                     disabled={saving}
                     className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                     data-testid="task-drawer-status">
              {["todo", "in_progress", "done", "skipped"].map(s => (
                <option key={s} value={s}>
                  {COLUMNS.find(c => c.key === s).label}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Priority">
            <select value={t.priority || "p2"} onChange={(e) => save({ priority: e.target.value })}
                     disabled={saving}
                     className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                     data-testid="task-drawer-priority">
              <option value="p0">P0 · urgent</option>
              <option value="p1">P1 · high</option>
              <option value="p2">P2 · normal</option>
            </select>
          </Row>
          <Row label="Owner">
            <input type="email" defaultValue={t.owner || ""}
                    onBlur={(e) => e.target.value !== t.owner && save({ owner: e.target.value })}
                    className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                    data-testid="task-drawer-owner"
                    placeholder="who@company.com" />
            {role === "client" && <div className="text-[11px] text-slate-500 mt-1">
              You can reassign only tasks you created.
            </div>}
          </Row>
          <Row label="Due date">
            <input type="date" defaultValue={t.due || ""}
                    onBlur={(e) => e.target.value !== t.due && save({ due: e.target.value })}
                    className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                    data-testid="task-drawer-due" />
          </Row>
          <Row label={<span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Notes</span>}>
            <textarea defaultValue={t.notes || ""} rows={3}
                       onBlur={(e) => e.target.value !== (t.notes || "") && save({ notes: e.target.value })}
                       className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                       data-testid="task-drawer-notes"
                       placeholder="What did you do? What's blocking?" />
          </Row>
          <Row label={<span className="inline-flex items-center gap-1"><History className="w-3.5 h-3.5" /> Activity</span>}>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="task-drawer-log">
              {log.length === 0 && <div className="text-xs text-slate-500">No activity yet.</div>}
              {log.map((l, i) => (
                <div key={i} className="text-xs text-slate-700 border-l-2 border-slate-200 pl-2 py-0.5">
                  <div>
                    <b>{(l.actor_email || "system").split("@")[0]}</b> {l.action.replace("_", " ")}
                    <span className="text-slate-400"> · {new Date(l.at).toLocaleString()}</span>
                  </div>
                  {Object.keys(l.after || {}).length > 0 && (
                    <div className="text-slate-500 mt-0.5 text-[11px]">
                      {Object.entries(l.after)
                        .filter(([k]) => k !== "updated_at")
                        .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Row>
          <div className="pt-2">
            <button onClick={onSaved} data-testid="task-drawer-done"
                    className="w-full rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold inline-flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Done — refresh board
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="appearance-none rounded-lg ring-1 ring-slate-200 bg-white pl-3 pr-7 py-1.5 text-xs font-bold text-slate-700"
              data-testid={testid}>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2 pointer-events-none" />
    </div>
  );
}

function BulkPanel({ count, onClose, onApply }) {
  const [status, setStatus]     = useState("");
  const [owner, setOwner]       = useState("");
  const [due, setDue]           = useState("");
  const [priority, setPriority] = useState("");

  const apply = () => {
    const patch = {};
    if (status)   patch.status   = status;
    if (owner)    patch.owner    = owner;
    if (due)      patch.due      = due;
    if (priority) patch.priority = priority;
    if (!Object.keys(patch).length) return toast.error("Pick a field to change");
    onApply(patch);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         data-testid="bulk-panel">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Bulk actions · {count} task(s)</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"
                  data-testid="bulk-panel-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Row label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                     className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                     data-testid="bulk-status">
              <option value="">— no change —</option>
              {["todo", "in_progress", "done", "skipped"].map(s => (
                <option key={s} value={s}>{COLUMNS.find(c => c.key === s).label}</option>
              ))}
            </select>
          </Row>
          <Row label="Owner">
            <input type="email" value={owner} onChange={(e) => setOwner(e.target.value)}
                    className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                    data-testid="bulk-owner"
                    placeholder="assign@company.com" />
          </Row>
          <Row label="Due date">
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                    className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                    data-testid="bulk-due" />
          </Row>
          <Row label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
                     className="rounded-lg ring-1 ring-slate-200 px-3 py-1.5 text-sm w-full"
                     data-testid="bulk-priority">
              <option value="">— no change —</option>
              <option value="p0">P0</option><option value="p1">P1</option><option value="p2">P2</option>
            </select>
          </Row>
          <div className="pt-2 flex gap-2">
            <button onClick={onClose} data-testid="bulk-cancel"
                    className="flex-1 rounded-lg ring-1 ring-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={apply} data-testid="bulk-apply"
                    className="flex-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
